/**
 * Submissions Store
 * Manages pending submissions for curators/admins
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from './auth.store'
import { cmApi } from '@/lib/api'

import type { SubmissionItem, SubmissionStatus } from '@/types/submission.types'

export const useSubmissionsStore = defineStore('submissions', () => {
    const submissions = ref<SubmissionItem[]>([])
    const isLoading = ref(false)
    const error = ref<string | null>(null)

    // Computed
    const pendingSubmissions = computed(() =>
        submissions.value.filter(s => s.status === 'pending')
    )

    const pendingCount = computed(() => pendingSubmissions.value.length)

    const approvedSubmissions = computed(() =>
        submissions.value.filter(s => s.status === 'approved')
    )

    const rejectedSubmissions = computed(() =>
        submissions.value.filter(s => s.status === 'rejected')
    )

    // Actions
    async function loadAllSubmissions() {
        isLoading.value = true
        error.value = null
        try {
            // Fetch pending/rejected opportunities from API (allStatuses=true)
            // Posts and resources still use direct Supabase for now
            const [oppsRes, postsResult, resResult] = await Promise.all([
                cmApi.get('/opportunities', { params: { allStatuses: true } }),
                supabase
                    .from('community_posts')
                    .select('*, author:profiles!community_posts_author_id_fkey(name)')
                    .not('status', 'is', null)
                    .neq('status', 'approved')
                    .order('created_at', { ascending: false }),
                supabase
                    .from('resources')
                    .select('*, submitter:profiles!resources_submitted_by_fkey(name)')
                    .not('status', 'is', null)
                    .neq('status', 'approved')
                    .order('created_at', { ascending: false }),
            ])

            const items: SubmissionItem[] = []

            // Map opportunities (only those that aren't approved)
            if (oppsRes.data) {
                const filteredOpps = (oppsRes.data as any[]).filter(o => o.status !== 'approved')
                for (const opp of filteredOpps) {
                    items.push({
                        id: opp.id,
                        type: 'opportunity',
                        title: opp.title,
                        description: opp.description,
                        status: opp.status as SubmissionStatus,
                        submitted_by_name: opp.submitted_by_name || 'Unknown',
                        submitted_at: opp.created_at,
                        reviewed_at: opp.reviewed_at,
                        data: opp,
                    })
                }
            }


            // Map community posts
            if (postsResult.data) {
                for (const post of postsResult.data) {
                    items.push({
                        id: post.id,
                        type: 'community_update',
                        title: post.title,
                        description: post.content,
                        status: post.status as SubmissionStatus,
                        submitted_by_name: (post.author as any)?.name || 'Unknown',
                        submitted_at: post.created_at,
                        reviewed_at: post.reviewed_at,
                        data: {
                            ...post,
                            author_name: (post.author as any)?.name || 'Anonymous',
                        },
                    })
                }
            }

            // Map resources
            if (resResult.data) {
                for (const res of resResult.data) {
                    items.push({
                        id: res.id,
                        type: 'resource',
                        title: res.title,
                        description: res.description,
                        status: res.status as SubmissionStatus,
                        submitted_by_name: (res.submitter as any)?.name || 'Unknown',
                        submitted_at: res.created_at,
                        reviewed_at: res.reviewed_at,
                        data: {
                            ...res,
                            submitted_by_name: (res.submitter as any)?.name || null,
                            submitter: undefined,
                        },
                    })
                }
            }

            // Sort by date descending
            items.sort((a, b) =>
                new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime()
            )

            submissions.value = items
        } catch (e: any) {
            console.error('Failed to load submissions:', e)
            error.value = e.message
        } finally {
            isLoading.value = false
        }
    }

    async function approveSubmission(type: SubmissionItem['type'], id: string) {
        const authStore = useAuthStore()
        if (!authStore.profile) return { success: false, error: 'Not authenticated' }

        try {
            if (type === 'opportunity') {
                await cmApi.patch(`/opportunities/${id}`, {
                    status: 'approved',
                    reviewed_by: authStore.profile.id,
                    reviewed_at: new Date().toISOString(),
                })
            } else {
                const table = type === 'resource' ? 'resources' : 'community_posts'
                const { error: dbError } = await supabase
                    .from(table)
                    .update({
                        status: 'approved',
                        reviewed_by: authStore.profile.id,
                        reviewed_at: new Date().toISOString(),
                    })
                    .eq('id', id)

                if (dbError) throw dbError
            }

            // Update local state
            const item = submissions.value.find(s => s.id === id)
            if (item) item.status = 'approved'

            return { success: true }
        } catch (e: any) {
            console.error('Failed to approve submission:', e)
            return { success: false, error: e.message }
        }

    }

    async function rejectSubmission(type: SubmissionItem['type'], id: string) {
        const authStore = useAuthStore()
        if (!authStore.profile) return { success: false, error: 'Not authenticated' }

        try {
            if (type === 'opportunity') {
                await cmApi.patch(`/opportunities/${id}`, {
                    status: 'rejected',
                    reviewed_by: authStore.profile.id,
                    reviewed_at: new Date().toISOString(),
                })
            } else {
                const table = type === 'resource' ? 'resources' : 'community_posts'
                const { error: dbError } = await supabase
                    .from(table)
                    .update({
                        status: 'rejected',
                        reviewed_by: authStore.profile.id,
                        reviewed_at: new Date().toISOString(),
                    })
                    .eq('id', id)

                if (dbError) throw dbError
            }

            // Update local state
            const item = submissions.value.find(s => s.id === id)
            if (item) item.status = 'rejected'

            return { success: true }
        } catch (e: any) {
            console.error('Failed to reject submission:', e)
            return { success: false, error: e.message }
        }

    }

    return {
        submissions,
        isLoading,
        error,
        pendingSubmissions,
        pendingCount,
        approvedSubmissions,
        rejectedSubmissions,
        loadAllSubmissions,
        approveSubmission,
        rejectSubmission,
    }
})
